import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Box, Divider, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { Fragment, memo, useContext, useMemo } from "react";
import { ReactComponent as LinkedInIcon } from "../../../assets/icons/linkedin.svg";
import { ReactComponent as LocationIcon } from '../../../assets/icons/location.svg';
import { ReactComponent as PersonIcon } from '../../../assets/icons/person.svg';
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { scrollbarStyle } from "../../../shared/dashboard";
import { Dashboard } from "../../../types/files";

const useStyles = makeStyles((theme) => ({
    card: {
        width: 450,
        maxWidth: 450,
        minHeight: 200,
        maxHeight: 400,
        height: 'auto',
        overflow: 'hidden',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: '0.05s ease-in-out',
    },
    container: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        ...scrollbarStyle,
    },
    name: {
        fontFamily: 'Inter',
        fontSize: '1.1rem',
        fontWeight: 700,
        color: theme.colors.neutral['700'],
    },
    infoText: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        color: theme.colors.neutral['600'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    infoLink: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        color: theme.palette.primary.main,
        textDecoration: 'none',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    infoIcon: {
        width: 18,
        height: 18,
        flexShrink: 0,
        color: theme.colors.neutral['400'],
    },
    linkedInIcon: {
        width: 18,
        height: 18,
        flexShrink: 0,
    },
    locationIcon: {
        width: 18,
        height: 18,
        flexShrink: 0,
        stroke: theme.colors.neutral['400'],
    },
    background: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        color: '#666666',
        whiteSpace: 'pre-line',
    },
    avatar: {
        width: 48,
        height: 48,
        minWidth: 48,
        borderRadius: '50%',
        background: '#d1fae5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    initials: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 600,
        color: '#065f46',
    },
}));

const PersonCard: React.FC<{ element: any }> = ({ element }) => {
    const classes = useStyles();

    const name = element.title?.split('|')?.[0]?.split('-')?.[0]?.trim();
    const initials = name
        ? name.split(' ').slice(0, 2).map((w: string) => w.charAt(0).toUpperCase()).join('')
        : '';

    const linkedInDisplay = useMemo(() => {
        if (!element.link) return null;
        try {
            const url = new URL(element.link);
            return url.hostname + url.pathname.replace(/\/$/, '');
        } catch {
            return element.link;
        }
    }, [element.link]);

    return (
        <Stack direction="row" spacing={2} p={2} width="100%">
            <Box className={classes.avatar}>
                <Typography className={classes.initials}>{initials}</Typography>
            </Box>
            <Stack direction="column" spacing={0.5} minWidth={0} flex={1}>
                <Typography className={classes.name}>{name || '[Person]'}</Typography>
                {!!element.role && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon className={classes.infoIcon} style={{ stroke: '#9ca3af' }} />
                        <Typography className={classes.infoText}>{element.role}</Typography>
                    </Stack>
                )}
                {!!element.email && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <MailOutlineIcon className={classes.infoIcon} />
                        <Typography className={classes.infoLink}
                            onClick={(e) => {
                                e.stopPropagation();
                                window.open(`mailto:${element.email}`, '_blank');
                            }}>
                            {element.email}
                        </Typography>
                    </Stack>
                )}
                {!!element.link && element.link.includes('linkedin') && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <LinkedInIcon className={classes.linkedInIcon} />
                        <Typography className={classes.infoLink}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                window.open(element.link, '_blank', 'noopener, noreferrer');
                            }}>
                            {linkedInDisplay}
                        </Typography>
                    </Stack>
                )}
                {!!element.location && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <LocationIcon className={classes.locationIcon} />
                        <Typography className={classes.infoText}>{element.location}</Typography>
                    </Stack>
                )}
                {!!element.content && (<>
                    <Divider sx={{ my: 1 }} />
                    <Typography className={classes.background}>
                        {element.content?.replace('- ', '• ')?.replaceAll('\n-', '\n •')}
                    </Typography>
                </>)}
            </Stack>
        </Stack>
    );
};

const KeyPeopleContentCard: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { mappedOverviewQueries } = useContext(DashboardsContext);

    const keyPeople = useMemo(() =>
        mappedOverviewQueries.get(`${dashboard.id}:Key People`)
    , [dashboard.id, mappedOverviewQueries]);

    if (!Array.isArray(keyPeople) || keyPeople.length === 0)
        return (<></>);

    return (<>
        <Stack className={classes.card}>
            <Stack className={classes.container} alignItems="flex-start" justifyContent="stretch">
                {keyPeople?.map((element: any, i) => (
                    <Fragment key={'key-people-content-card-196-' + i}>
                        {(i > 0) && <Divider sx={{ width: '100%' }} />}
                        <PersonCard element={element} />
                    </Fragment>
                ))}
            </Stack>
        </Stack>
    </>);
};

export default memo(KeyPeopleContentCard);
